import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { StoredFile } from '@/features/students/types'
import type {
  CallInfo,
  CallJoin,
  ChatMessage,
  ClassRoomRow,
  Contact,
  ConversationDetail,
  ConversationRow,
  MessagingSummary,
} from './types'

export const messagingKeys = {
  all: ['messaging'] as const,
  summary: () => [...messagingKeys.all, 'summary'] as const,
  conversations: () => [...messagingKeys.all, 'conversations'] as const,
  conversation: (id: number) => [...messagingKeys.all, 'conversation', id] as const,
  messages: (id: number) => [...messagingKeys.all, 'messages', id] as const,
  contacts: (q: string) => [...messagingKeys.all, 'contacts', q] as const,
  classRooms: () => [...messagingKeys.all, 'class-rooms'] as const,
}

/** Unread count and ongoing calls, polled everywhere in the app (incoming calls ring from here). */
export const summaryQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: messagingKeys.summary(),
    queryFn: () => api.get<MessagingSummary>('/messaging/summary'),
    enabled,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    staleTime: 2000,
  })

export const conversationsQuery = () =>
  queryOptions({
    queryKey: messagingKeys.conversations(),
    queryFn: () => api.get<ConversationRow[]>('/messaging/conversations'),
    refetchInterval: 8000,
  })

export const conversationQuery = (id: number) =>
  queryOptions({
    queryKey: messagingKeys.conversation(id),
    queryFn: () => api.get<ConversationDetail>(`/messaging/conversations/${id}`),
    refetchInterval: 10000,
  })

/** The latest messages of a conversation, polled while it is open. */
export const latestMessagesQuery = (id: number) =>
  queryOptions({
    queryKey: messagingKeys.messages(id),
    queryFn: () =>
      api.get<ChatMessage[]>(`/messaging/conversations/${id}/messages`, { params: { limit: 50 } }),
    refetchInterval: 3000,
  })

export function fetchOlderMessages(id: number, before: number): Promise<ChatMessage[]> {
  return api.get<ChatMessage[]>(`/messaging/conversations/${id}/messages`, { params: { before, limit: 50 } })
}

export const contactsQuery = (q: string) =>
  queryOptions({
    queryKey: messagingKeys.contacts(q),
    queryFn: () => api.get<Contact[]>('/messaging/contacts', { params: q ? { q } : undefined }),
    staleTime: 30_000,
  })

export const classRoomsQuery = () =>
  queryOptions({
    queryKey: messagingKeys.classRooms(),
    queryFn: () => api.get<ClassRoomRow[]>('/messaging/class-rooms'),
  })

export function fileLink(
  id: number,
  inline = false,
): Promise<{ url: string; name: string; mimeType: string }> {
  return api.get(`/messaging/files/${id}/link`, { params: inline ? { inline: 1 } : undefined })
}

export function uploadAttachment(file: File, onProgress?: (percent: number) => void): Promise<StoredFile> {
  const body = new FormData()
  body.append('file', file)
  return api.post<StoredFile>('/messaging/files', body, {
    timeout: 0,
    onUploadProgress: (e) => (e.total ? onProgress?.(Math.round((e.loaded / e.total) * 100)) : undefined),
  })
}

function useMessagingMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: messagingKeys.all }),
  })
}

export const useOpenDirect = () =>
  useMessagingMutation((userId: number) =>
    api.post<ConversationDetail>('/messaging/conversations/direct', { userId }),
  )
export const useCreateGroup = () =>
  useMessagingMutation((body: { title: string; userIds: number[] }) =>
    api.post<ConversationDetail>('/messaging/conversations/group', body),
  )
export const useOpenClassRoom = () =>
  useMessagingMutation((sectionId: number) =>
    api.post<ConversationDetail>(`/messaging/class-rooms/${sectionId}`),
  )
export const useUpdateConversation = (id: number) =>
  useMessagingMutation((body: { title?: string; studentsCanPost?: boolean }) =>
    api.put<ConversationDetail>(`/messaging/conversations/${id}`, body),
  )
export const useAddMembers = (id: number) =>
  useMessagingMutation((userIds: number[]) =>
    api.post<ConversationDetail>(`/messaging/conversations/${id}/members`, { userIds }),
  )
export const useRemoveMember = (id: number) =>
  useMessagingMutation((userId: number) => api.delete(`/messaging/conversations/${id}/members/${userId}`))
export const useSendMessage = (id: number) =>
  useMessagingMutation((body: { body?: string; fileIds?: number[] }) =>
    api.post<ChatMessage>(`/messaging/conversations/${id}/messages`, body),
  )
export const useEditMessage = () =>
  useMessagingMutation(({ id, body }: { id: number; body: string }) =>
    api.put<ChatMessage>(`/messaging/messages/${id}`, { body }),
  )
export const useDeleteMessage = () =>
  useMessagingMutation((id: number) => api.delete(`/messaging/messages/${id}`))

export function markRead(id: number, messageId: number): Promise<void> {
  return api.post(`/messaging/conversations/${id}/read`, { messageId })
}

export function startCall(conversationId: number, video: boolean): Promise<CallJoin> {
  return api.post<CallJoin>(`/messaging/conversations/${conversationId}/calls`, { video })
}
export function joinCall(callId: number): Promise<CallJoin> {
  return api.post<CallJoin>(`/messaging/calls/${callId}/join`)
}
export function endCall(callId: number): Promise<CallInfo> {
  return api.post<CallInfo>(`/messaging/calls/${callId}/end`)
}
